import type { BackofficePermissionCode } from '@kardume/types';

type PermissionDefinition = {
  group: string;
  label: string;
  description: string;
};

/** UI metadata (group/label/description) for each backoffice permission code. */
export const PERMISSION_DEFINITIONS: Record<BackofficePermissionCode, PermissionDefinition> = {
  ROLE_BACKOFFICE_DASHBOARD_READ: {
    group: 'Operação',
    label: 'Ver dashboard',
    description: 'Visualizar indicadores e o resumo operacional do backoffice',
  },
  ROLE_BACKOFFICE_MERCHANT_READ: {
    group: 'Operação',
    label: 'Ver lojistas',
    description: 'Consultar a lista e os detalhes dos lojistas',
  },
  ROLE_BACKOFFICE_MERCHANT_MANAGE: {
    group: 'Operação',
    label: 'Gerenciar lojistas',
    description: 'Criar, editar e alterar o status dos lojistas',
  },
  ROLE_BACKOFFICE_CUSTOMER_READ: {
    group: 'Operação',
    label: 'Ver usuários',
    description: 'Consultar os usuários do aplicativo',
  },
  ROLE_BACKOFFICE_CUSTOMER_MANAGE: {
    group: 'Operação',
    label: 'Gerenciar usuários',
    description: 'Alterar o status dos usuários do aplicativo',
  },
  // A descrição cita as duas telas porque a permissão é uma só: quem marca esta
  // caixa também ganha o catálogo de regras. Sem isso, quem monta um perfil de
  // acesso concede a escrita do catálogo achando que liberou só a moderação.
  ROLE_BACKOFFICE_PROMOTION_MODERATE: {
    group: 'Operação',
    label: 'Moderar anúncios',
    description:
      'Aprovar ou rejeitar anúncios e promoções, manter o catálogo de regras de anúncio e o catálogo de tipos de benefício',
  },
  // Card #352 - grupo Operação, que é onde a fila mora no menu. O grupo precisa
  // existir em PERMISSION_GROUP_ORDER, senão o `indexOf` devolve -1 e a
  // ordenação do construtor de perfil quebra em silêncio.
  ROLE_BACKOFFICE_EXPERIENCE_MODERATE: {
    group: 'Operação',
    label: 'Moderar experiências',
    description:
      'Aprovar ou recusar as experiências enviadas pelas lojas. É uma fila própria, com critério próprio: aqui se confere se a descrição corresponde ao que a pessoa vai viver, e nada aparece na área Experiências sem passar por ela',
  },
  // Comercial, e não Operação: o menu agrupa Leads, Oportunidades e Disparos
  // como o funil, e o construtor de perfil precisa falar a mesma língua da
  // tela. A descrição cita as duas telas porque a permissão é uma só - quem
  // marca "gerenciar leads" também ganha a escrita de Oportunidades.
  ROLE_BACKOFFICE_LEAD_READ: {
    group: 'Comercial',
    label: 'Ver leads e oportunidades',
    description: 'Consultar o board de leads captados pelo site e a lista de oportunidades de prospecção',
  },
  ROLE_BACKOFFICE_LEAD_MANAGE: {
    group: 'Comercial',
    label: 'Gerenciar leads e oportunidades',
    description:
      'Mover leads entre status, editar anotações, remover leads e registrar o resultado de uma oportunidade (gerar lead, marcar como parceira ou descartar)',
  },
  ROLE_BACKOFFICE_MESSAGING_READ: {
    group: 'Comercial',
    label: 'Ver disparos',
    description: 'Consultar instâncias, listas, templates e campanhas de disparo',
  },
  ROLE_BACKOFFICE_MESSAGING_MANAGE: {
    group: 'Comercial',
    label: 'Gerenciar disparos',
    description: 'Configurar a instância e seus limites, montar listas e templates e criar campanhas',
  },
  ROLE_BACKOFFICE_MESSAGING_SEND: {
    group: 'Comercial',
    label: 'Disparar mensagens',
    description: 'Iniciar, pausar e cancelar campanhas e enviar mensagem de teste',
  },
  ROLE_BACKOFFICE_PARTNERSHIP_MANAGE: {
    group: 'Operação',
    label: 'Gerenciar parceria',
    description: 'Ativar, suspender, reativar e encerrar a parceria de uma empresa, e operar destaques',
  },
  ROLE_BACKOFFICE_CLAIM_READ: {
    group: 'Operação',
    label: 'Ver reivindicações',
    description:
      'Ver a fila e o detalhe de quem pediu para assumir a página de uma empresa - inclui o nome, o e-mail e o telefone declarados por essa pessoa',
  },
  ROLE_BACKOFFICE_CLAIM_MANAGE: {
    group: 'Operação',
    label: 'Decidir reivindicações',
    description:
      'Aprovar, recusar, pedir informação e revogar. Aprovar entrega a página de um negócio real a quem pediu; revogar tira de quem já a administrava',
  },
  // As duas do card 253 ficam em Operação, ao lado das de reivindicação: quem
  // monta um perfil procura por elas junto do resto da fila.
  ROLE_BACKOFFICE_CLAIM_EVIDENCE_READ: {
    group: 'Operação',
    label: 'Abrir documentos anexados',
    description:
      'Abrir o contrato social, o cartão CNPJ ou a procuração anexados a um pedido. Sem ela a pessoa vê que o anexo existe, mas não o conteúdo - e toda abertura fica registrada com quem, quando e de onde',
  },
  ROLE_BACKOFFICE_PROFILE_EDIT_MODERATE: {
    group: 'Operação',
    label: 'Moderar edições do responsável',
    description:
      'Aprovar ou recusar as mudanças de nome, endereço e categoria propostas por quem administra a página. Aprovar muda o que o guia diz sobre um negócio real, para todo visitante, na hora',
  },
  // Spec 038 - grupo Financeiro, que é onde a tela mora no menu. As duas são
  // próprias e não derivam de `BILLING_MANAGE`: este é o único lugar do
  // backoffice onde se lê quanto cada pessoa física recebe, e conceder isso de
  // carona com a cobrança do lojista entregaria o dado a quem nunca pediu.
  ROLE_BACKOFFICE_INFLUENCER_READ: {
    group: 'Financeiro',
    label: 'Ver influenciadores',
    description:
      'Consultar contratos, tabela de valores por plano, comissões apuradas e fechamentos de pagamento',
  },
  ROLE_BACKOFFICE_INFLUENCER_MANAGE: {
    group: 'Financeiro',
    label: 'Gerenciar influenciadores',
    description:
      'Promover um cliente a influenciador, definir quanto cada plano paga, cancelar comissão e registrar pagamento. Escrever aqui cria dívida da empresa com uma pessoa e declara que ela foi paga',
  },
  ROLE_BACKOFFICE_CATEGORY_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar categorias',
    description: 'Criar e editar as categorias do catálogo',
  },
  // Card 305 - grupo Plataforma, que é onde a tela de Comodidades mora no menu.
  // O grupo precisa existir em PERMISSION_GROUP_ORDER, senão o `indexOf` devolve
  // -1 e a ordenação do construtor de perfil quebra em silêncio.
  ROLE_BACKOFFICE_AMENITY_READ: {
    group: 'Plataforma',
    label: 'Ver comodidades',
    description: 'Consultar o catálogo de comodidades oferecidas às lojas',
  },
  ROLE_BACKOFFICE_AMENITY_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar comodidades',
    description:
      'Criar, editar, ativar/desativar e excluir comodidades. Criar uma acrescenta a caixa no perfil de todo lojista, e desativar tira a etiqueta da vitrine de quem já a marcou',
  },
  // Card 311 - mesmo grupo Plataforma, que já existe em PERMISSION_GROUP_ORDER.
  // Com um grupo novo o `indexOf` devolveria -1 e a ordenação do construtor de
  // perfil quebraria em silêncio.
  ROLE_BACKOFFICE_PAYMENT_METHOD_READ: {
    group: 'Plataforma',
    label: 'Ver formas de pagamento',
    description: 'Consultar o catálogo de formas de pagamento oferecidas às lojas',
  },
  ROLE_BACKOFFICE_PAYMENT_METHOD_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar formas de pagamento',
    description:
      'Criar, editar, ativar/desativar e excluir formas de pagamento. Criar uma acrescenta a caixa no perfil de todo lojista, e desativar tira a etiqueta da vitrine de quem já a declarou',
  },
  // Card 346 - grupo Comercial, que é onde a tela mora no menu (ao lado de
  // "Interesse por empresa") e que já existe em PERMISSION_GROUP_ORDER.
  ROLE_BACKOFFICE_SEARCH_TERM_READ: {
    group: 'Comercial',
    label: 'Ver termos buscados',
    description:
      'Consultar o que as pessoas digitaram na busca da vitrine e não encontraram. É texto escrito por visitantes, por isso a permissão é separada da de relatórios: quem vê audiência agregada não vê necessariamente o que foi digitado',
  },
  ROLE_BACKOFFICE_LOCALITY_READ: {
    group: 'Plataforma',
    label: 'Ver localidades',
    description: 'Consultar as cidades onde o Kardume opera',
  },
  ROLE_BACKOFFICE_LOCALITY_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar localidades',
    description: 'Criar, editar, ativar/desativar e excluir cidades atendidas',
  },
  ROLE_BACKOFFICE_FEED_READ: {
    group: 'Plataforma',
    label: 'Ver matérias',
    description: 'Consultar as matérias e destaques das páginas de cidade',
  },
  ROLE_BACKOFFICE_FEED_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar matérias',
    description: 'Escrever, publicar, agendar e excluir matérias das páginas de cidade',
  },
  ROLE_BACKOFFICE_COLLECTION_READ: {
    group: 'Plataforma',
    label: 'Ver coleções',
    description: 'Consultar as listas curadas publicadas em /colecoes',
  },
  ROLE_BACKOFFICE_COLLECTION_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar coleções',
    description: 'Montar, ordenar, publicar e excluir as listas curadas de /colecoes',
  },
  ROLE_BACKOFFICE_PLAN_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar planos',
    description: 'Criar, editar, ativar e arquivar planos comerciais',
  },
  ROLE_BACKOFFICE_PASS_PLAN_MANAGE: {
    group: 'Plataforma',
    label: 'Gerenciar Kardume Pass',
    description: 'Criar, editar, ativar e arquivar os planos do consumidor (Kardume Pass)',
  },
  ROLE_BACKOFFICE_SUBSCRIPTION_MANAGE: {
    group: 'Financeiro',
    label: 'Gerenciar assinaturas',
    description: 'Controlar as assinaturas dos lojistas',
  },
  ROLE_BACKOFFICE_BILLING_MANAGE: {
    group: 'Financeiro',
    label: 'Gerenciar financeiro',
    description: 'Configurar cobrança, carência e inadimplência',
  },
  // A descrição cita as duas telas porque a permissão é uma só: quem marca esta
  // caixa também ganha "Interesse por empresa", em Comercial. Sem isso, quem
  // monta um perfil acha que liberou só o financeiro.
  ROLE_BACKOFFICE_REPORT_READ: {
    group: 'Financeiro',
    label: 'Ver relatórios',
    description: 'Consultar os relatórios financeiros e o mapa de interesse por empresa',
  },
  ROLE_BACKOFFICE_AUDIT_READ: {
    group: 'Acesso',
    label: 'Ver logs',
    description: 'Consultar a trilha de auditoria e eventos administrativos',
  },
  ROLE_BACKOFFICE_EMPLOYEE_READ: {
    group: 'Acesso',
    label: 'Ver funcionários',
    description: 'Visualizar os usuários do backoffice',
  },
  ROLE_BACKOFFICE_EMPLOYEE_MANAGE: {
    group: 'Acesso',
    label: 'Gerenciar funcionários',
    description: 'Criar e editar os usuários do backoffice',
  },
  ROLE_BACKOFFICE_ACCESS_PROFILE_MANAGE: {
    group: 'Acesso',
    label: 'Gerenciar perfis de acesso',
    description: 'Criar perfis de acesso e editar suas permissões',
  },
};

export const PERMISSION_GROUP_ORDER = ['Acesso', 'Comercial', 'Financeiro', 'Operação', 'Plataforma'];

export const getPermissionDefinition = (code: BackofficePermissionCode): PermissionDefinition =>
  PERMISSION_DEFINITIONS[code] || { group: 'Outros', label: code, description: code };

export const sortPermissionCodes = (codes: BackofficePermissionCode[]): BackofficePermissionCode[] =>
  [...codes].sort((a, b) => {
    const da = getPermissionDefinition(a);
    const db = getPermissionDefinition(b);
    const groupDiff = PERMISSION_GROUP_ORDER.indexOf(da.group) - PERMISSION_GROUP_ORDER.indexOf(db.group);
    if (groupDiff !== 0) return groupDiff;
    return da.label.localeCompare(db.label, 'pt-BR');
  });

export const formatPermissionLabel = (code: BackofficePermissionCode): string => getPermissionDefinition(code).label;
export const formatPermissionDescription = (code: BackofficePermissionCode): string =>
  getPermissionDefinition(code).description;
export const formatPermissionGroup = (code: BackofficePermissionCode): string => getPermissionDefinition(code).group;

